/**
 * Who sees which line of this work's history, and which fields of it — OND-79.
 *
 * A FILE OF ITS OWN RATHER THAN MORE OF `work.model.ts`, which is already
 * fifteen hundred lines. Everything here is pure: it turns the one response
 * `GET /v1/works/:id/disclosure` returns into the rows the panel draws, and
 * decides nothing about access.
 *
 * IT MIRRORS NO RULE. The evaluation order — default, then the audience
 * override that replaces it, then the entry restrictions and any narrower agent
 * setting — lives in `ondo.resolve_entry_disclosures()` and is deliberately NOT
 * reimplemented here. What this file shows is the rules the owner has WRITTEN,
 * which is a different and much smaller question: a panel that computed the
 * effective answer would be a second copy of the precedence rule, and the one
 * it would disagree with is the one enforcing it.
 */

import { DISCLOSURE_AUDIENCES } from "@ondo/shared";
import type {
  DisclosureAudience,
  ProvenanceEntry,
  ProvenanceField,
  WorkDisclosureResponse,
  WorkDisclosureRule,
} from "@ondo/shared";

import {
  DISCLOSURE_AUDIENCE_LABELS,
  PROVENANCE_FIELD_LABELS,
  WORK_DISCLOSURE_COPY,
} from "./work.constants.ts";

/** One checkbox: a field, and whether this rule withholds it. */
export interface FieldChoice {
  field: ProvenanceField;
  label: string;
  hidden: boolean;
}

/**
 * One row of the panel: a rule the owner or an agent has written.
 *
 * `audience: null` is the default every authorised viewer inherits, and it
 * sorts first — the panel reads top to bottom in the order the rules apply.
 */
export interface DisclosureRow {
  ruleId: string;
  /** The default's own heading, or the audience's label. */
  heading: string;
  /** Which line of the history, or null for the whole record. */
  entryId: string | null;
  /** The entry's own one-line description, or the all-entries sentence. */
  scope: string;
  hidden: boolean;
  hiddenFields: ProvenanceField[];
  /** The field labels this rule withholds, for the row's summary line. */
  summary: string;
  /**
   * Whether a delegated agent wrote it.
   *
   * The panel marks these rather than hiding them: an agent's rules outlive the
   * revocation of their mandate — ending them would be a widening performed by
   * a revocation — so the owner has to be able to see which are whose.
   */
  byAgent: boolean;
  /** Whether the caller may remove this row. An agent may remove only their own. */
  canRemove: boolean;
}

/** The audiences that have NO override, and are therefore on the default. */
export interface InheritingAudience {
  audience: DisclosureAudience;
  label: string;
}

export interface DisclosurePanel {
  /** Whether to draw the editing controls at all. */
  canManage: boolean;
  /** The sentence at the top, which differs for an agent. */
  lead: string;
  rows: DisclosureRow[];
  /**
   * The audiences inheriting the default, named rather than counted.
   *
   * INHERITANCE IS THE ABSENCE OF A ROW, so there is nothing in the response
   * that says "gallery inherits" — it is the audiences with no rule of their
   * own, computed here from the enum. Naming them is what makes "removing an
   * override restores inheritance" visible: the audience moves from the rows
   * into this line.
   */
  inheriting: InheritingAudience[];
  /** How many live appointments may narrow these rules. Null when none can. */
  delegates: number;
  /** The fields a new rule can withhold, all unticked. */
  fields: FieldChoice[];
  /** The audiences a new rule can be written for, the default first. */
  audiences: InheritingAudience[];
}

/**
 * The panel, from the response and the chain the record already holds.
 *
 * THE ENTRIES COME FROM THE CHAIN AND NOT FROM A SECOND CALL. A rule names an
 * entry by id and the owner needs to read which line that is; the work record
 * has already loaded its history, so passing it in is one fewer request and one
 * fewer place for the two to disagree. An entry the chain does not carry — one
 * the rule hides from the caller's own audience, which an agent's rule can
 * do — reads as the fallback sentence rather than as a blank.
 */
export function toDisclosurePanel(
  response: WorkDisclosureResponse | null,
  entries: ProvenanceEntry[],
): DisclosurePanel | null {
  if (response === null) return null;

  const described = new Map(entries.map((entry) => [entry.id, describeEntry(entry)]));
  const overridden = new Set(
    response.rules
      .filter((rule) => rule.audience !== null)
      .map((rule) => rule.audience as DisclosureAudience),
  );

  return {
    canManage: response.controls.canManage,
    lead: response.controls.asAgent
      ? WORK_DISCLOSURE_COPY.leadAgent
      : WORK_DISCLOSURE_COPY.leadOwner,
    rows: [...response.rules].sort(byApplicationOrder).map((rule) => ({
      ruleId: rule.ruleId,
      heading:
        rule.audience === null
          ? WORK_DISCLOSURE_COPY.defaultHeading
          : DISCLOSURE_AUDIENCE_LABELS[rule.audience],
      entryId: rule.entryId,
      scope:
        rule.entryId === null
          ? WORK_DISCLOSURE_COPY.everyEntry
          : (described.get(rule.entryId) ?? WORK_DISCLOSURE_COPY.entryWithheld),
      hidden: rule.hidden,
      hiddenFields: rule.hiddenFields,
      summary: summarise(rule),
      byAgent: rule.byAgent,
      // An agent may remove a row the delegated side wrote and not the owner's.
      // The same expression the door uses, so the panel never offers a button
      // the database refuses — and the owner, who may remove either, is the
      // `!asAgent` half.
      canRemove:
        response.controls.canManage && (!response.controls.asAgent || rule.byAgent),
    })),
    inheriting: DISCLOSURE_AUDIENCES.filter((audience) => !overridden.has(audience)).map(
      (audience) => ({ audience, label: DISCLOSURE_AUDIENCE_LABELS[audience] }),
    ),
    delegates: response.controls.delegates,
    fields: EMPTY_FIELD_CHOICES,
    audiences: DISCLOSURE_AUDIENCES.map((audience) => ({
      audience,
      label: DISCLOSURE_AUDIENCE_LABELS[audience],
    })),
  };
}

/**
 * The order the rules apply in, which is the order they are drawn in.
 *
 * The default first, then the audience overrides; work-wide rules before the
 * per-entry ones. It is the ticket's own evaluation order, used here as a
 * SORT and not as a computation — the panel shows what was written and the
 * database decides what it means.
 */
function byApplicationOrder(a: WorkDisclosureRule, b: WorkDisclosureRule): number {
  if ((a.entryId === null) !== (b.entryId === null)) return a.entryId === null ? -1 : 1;
  if ((a.audience === null) !== (b.audience === null)) return a.audience === null ? -1 : 1;
  if (a.audience !== null && b.audience !== null && a.audience !== b.audience) {
    return (
      DISCLOSURE_AUDIENCES.indexOf(a.audience) - DISCLOSURE_AUDIENCES.indexOf(b.audience)
    );
  }
  return a.createdAt.localeCompare(b.createdAt);
}

/**
 * What one rule withholds, in words.
 *
 * THE EMPTY RULE HAS ITS OWN SENTENCE and it is not "nothing". At work level an
 * override REPLACES the default, so a rule that withholds no field is the way
 * to let an audience see everything the default hides — a legitimate thing to
 * want, and a dangerous thing to render as "no restrictions" beside a default
 * that has several.
 */
function summarise(rule: WorkDisclosureRule): string {
  if (rule.hidden) return WORK_DISCLOSURE_COPY.wholeEntryHidden;
  if (rule.hiddenFields.length === 0) {
    return rule.audience === null
      ? WORK_DISCLOSURE_COPY.nothingWithheld
      : WORK_DISCLOSURE_COPY.overrideRevealsAll;
  }

  return rule.hiddenFields.map((field) => PROVENANCE_FIELD_LABELS[field]).join(", ");
}

/** One line naming a history entry, for an owner choosing which to restrict. */
function describeEntry(entry: ProvenanceEntry): string {
  const when = entry.startText ?? entry.createdAt.slice(0, 4);
  const what = entry.titleText ?? entry.venueName ?? entry.ownerName ?? entry.insurerName;

  return what === null || what === undefined
    ? `${entry.kind} · ${when}`
    : `${entry.kind} · ${what} · ${when}`;
}

/**
 * The unticked field list, built once.
 *
 * `PROVENANCE_FIELDS` ORDER AND NOT ALPHABETICAL, because that is the order the
 * database stores a saved rule in — `array_agg(distinct f order by f)` over the
 * enum — so a rule saved from this form redraws in the order it was ticked in.
 */
const EMPTY_FIELD_CHOICES: FieldChoice[] = [
  { field: "price", label: PROVENANCE_FIELD_LABELS.price, hidden: false },
  { field: "counterparty", label: PROVENANCE_FIELD_LABELS.counterparty, hidden: false },
  { field: "insurer", label: PROVENANCE_FIELD_LABELS.insurer, hidden: false },
  {
    field: "policy_reference",
    label: PROVENANCE_FIELD_LABELS.policy_reference,
    hidden: false,
  },
  {
    field: "insured_amount",
    label: PROVENANCE_FIELD_LABELS.insured_amount,
    hidden: false,
  },
  { field: "documents", label: PROVENANCE_FIELD_LABELS.documents, hidden: false },
];
